/**
 * Formatação de números exibidos ao usuário (dados de mapas, logs e análises).
 *
 * Regra: no máximo 3 casas decimais e NUNCA arredondar — o excedente é cortado
 * (truncamento em direção a zero). Ex.: 1,0049999 → "1,004"; -0,0359 → "-0,035".
 */

/** Máximo de casas decimais exibidas para dados de mapa/log/análise. */
export const MAX_DISPLAY_DECIMALS = 3;

export interface TruncatedFormatOptions {
  /** Mínimo de casas decimais (preenche com zeros). Padrão 0; limitado a maxDecimals. */
  minDecimals?: number;
  /** Máximo de casas decimais. Padrão e teto: MAX_DISPLAY_DECIMALS. */
  maxDecimals?: number;
  /** Locale do separador; undefined usa o locale do navegador. */
  locale?: string;
  /** Agrupamento de milhar. Padrão true (igual ao Intl). */
  useGrouping?: boolean;
  /** Mínimo de dígitos inteiros (preenche com zeros à esquerda). Padrão 1. */
  minIntegerDigits?: number;
}

// O lib do TS 5.4 não tipa `roundingMode` nem `signDisplay: 'negative'`.
type TruncNumberFormatOptions = Omit<Intl.NumberFormatOptions, 'signDisplay'> & {
  roundingMode: 'trunc';
  signDisplay: 'negative';
};

/** Texto devolvido para valor não finito (NaN/±Infinity): nunca "NaN" nem "∞" na tela. */
const NON_FINITE_TEXT = '—';

/**
 * `roundingMode` (Intl.NumberFormat v3) é ignorado em silêncio por navegadores antigos, que
 * então ARREDONDARIAM. Detecta uma vez pelo `resolvedOptions()`; sem suporte usa o caminho
 * de string (`formatTruncatedByString`).
 */
const SUPPORTS_TRUNC_ROUNDING: boolean = (() => {
  try {
    const opts = new Intl.NumberFormat('en-US', {
      roundingMode: 'trunc',
    } as unknown as Intl.NumberFormatOptions).resolvedOptions() as { roundingMode?: string };
    return opts.roundingMode === 'trunc';
  } catch {
    return false;
  }
})();

const formatterCache = new Map<string, Intl.NumberFormat>();

function getFormatter(
  locale: string | undefined,
  minDecimals: number,
  maxDecimals: number,
  useGrouping: boolean,
  minIntegerDigits: number,
): Intl.NumberFormat {
  const key = `${locale ?? ''}|${minDecimals}|${maxDecimals}|${useGrouping}|${minIntegerDigits}`;
  let fmt = formatterCache.get(key);
  if (!fmt) {
    const options: TruncNumberFormatOptions = {
      minimumFractionDigits: minDecimals,
      maximumFractionDigits: maxDecimals,
      minimumIntegerDigits: minIntegerDigits,
      useGrouping,
      roundingMode: 'trunc',
      // 'negative' evita "-0" quando o valor truncado vira zero (ex.: -0,0004).
      signDisplay: 'negative',
    };
    fmt = new Intl.NumberFormat(locale, options as unknown as Intl.NumberFormatOptions);
    formatterCache.set(key, fmt);
  }
  return fmt;
}

/** NaN cai no padrão; infinitos e fracionários são limitados/cortados. */
function clamp(n: number, min: number, max: number, fallback: number): number {
  if (Number.isNaN(n)) return fallback;
  return Math.max(min, Math.min(max, Math.floor(n)));
}

/** Separador decimal e sinal de menos do locale (via Intl, sem tabela própria). */
function localeSymbols(locale: string | undefined): { decimal: string; minus: string } {
  const decimal = new Intl.NumberFormat(locale).formatToParts(1.1).find((p) => p.type === 'decimal');
  const minus = new Intl.NumberFormat(locale).formatToParts(-1).find((p) => p.type === 'minusSign');
  return { decimal: decimal?.value ?? '.', minus: minus?.value ?? '-' };
}

/** Separa `String(|value|)` em parte inteira e fracionária, inclusive em notação exponencial. */
function splitDecimalDigits(abs: number): { int: string; frac: string } {
  const [mantissa, exp = '0'] = String(abs).split('e');
  const dot = mantissa.indexOf('.');
  const digits = mantissa.replace('.', '');
  // posição do ponto decimal dentro de `digits` depois de aplicar o expoente
  const point = (dot < 0 ? mantissa.length : dot) + Number(exp);
  if (point <= 0) return { int: '0', frac: '0'.repeat(-point) + digits };
  if (point >= digits.length) return { int: digits + '0'.repeat(point - digits.length), frac: '' };
  return { int: digits.slice(0, point), frac: digits.slice(point) };
}

/**
 * Caminho sem `roundingMode`: corta pelo texto de `String(value)` (menor decimal do double,
 * igual ao que o Intl usa) — e-notação pequena vira 0 com ≤ 3 casas; `e+` não tem decimais.
 * Exportado para o spec exercitar este caminho sem depender do navegador.
 */
export function formatTruncatedByString(value: number, opts: TruncatedFormatOptions = {}): string {
  if (!Number.isFinite(value)) return NON_FINITE_TEXT;
  const { maxDecimals, minDecimals, minIntegerDigits } = resolveOptions(opts);
  const { int, frac } = splitDecimalDigits(Math.abs(value));
  const keptFrac = frac.slice(0, maxDecimals).replace(/0+$/, '').padEnd(minDecimals, '0');
  const intFmt = new Intl.NumberFormat(opts.locale, {
    useGrouping: opts.useGrouping ?? true,
    minimumIntegerDigits: minIntegerDigits,
  });
  const { decimal, minus } = localeSymbols(opts.locale);
  const nonZero = /[1-9]/.test(int) || /[1-9]/.test(keptFrac);
  const sign = value < 0 && nonZero ? minus : '';
  // BigInt preserva inteiros acima de 2^53 sem passar por double.
  const intText = intFmt.format(BigInt(int) as unknown as number);
  return sign + intText + (keptFrac ? decimal + keptFrac : '');
}

function resolveOptions(opts: TruncatedFormatOptions) {
  const maxDecimals = clamp(opts.maxDecimals ?? MAX_DISPLAY_DECIMALS, 0, MAX_DISPLAY_DECIMALS, MAX_DISPLAY_DECIMALS);
  return {
    maxDecimals,
    minDecimals: clamp(opts.minDecimals ?? 0, 0, maxDecimals, 0),
    minIntegerDigits: clamp(opts.minIntegerDigits ?? 1, 1, 21, 1),
  };
}

/**
 * Formata `value` cortando (sem arredondar) as casas além de `maxDecimals`
 * (no máximo 3). O Intl trunca a partir da menor representação decimal do
 * double, então 0.29 continua "0,29" (sem o erro 0.28999… do `v * 1000`).
 * Valor não finito devolve "—". Sem suporte a `roundingMode` usa o corte por string.
 */
export function formatTruncated(value: number, opts: TruncatedFormatOptions = {}): string {
  if (!Number.isFinite(value)) return NON_FINITE_TEXT;
  if (!SUPPORTS_TRUNC_ROUNDING) return formatTruncatedByString(value, opts);
  const { maxDecimals, minDecimals, minIntegerDigits } = resolveOptions(opts);
  return getFormatter(
    opts.locale,
    minDecimals,
    maxDecimals,
    opts.useGrouping ?? true,
    minIntegerDigits,
  ).format(value);
}

/**
 * Trunca `value` para `decimals` casas (máx. 3) e devolve número — para campos
 * editáveis (mín/máx). Usa o caminho de string (en-US, sem agrupamento) para
 * evitar o erro de ponto flutuante de `Math.trunc(v * 10^d)` (0.29*1000 = 289.999…).
 */
export function truncateTo(value: number, decimals: number): number {
  if (!Number.isFinite(value)) return value;
  const text = formatTruncated(value, {
    maxDecimals: decimals,
    locale: 'en-US',
    useGrouping: false,
  });
  return Number(text);
}

/**
 * Limita as casas decimais de um texto JÁ formatado (ex.: `cell_texts` do editor). Corta
 * (sem arredondar) os dígitos após o ÚLTIMO '.' ou ',' só quando passam de `maxDecimals`.
 * Grupos de milhar têm sempre 3 dígitos, então nunca são tocados com o teto padrão de 3.
 * Os textos já chegam na precisão do software e são exibidos literalmente; isto é só um teto.
 *
 * Interação teto × literal: texto com MAIS de `maxDecimals` casas é cortado (nunca arredondado);
 * com até `maxDecimals` casas é devolvido byte a byte (zeros à direita preservados, "1.700").
 * Efeito colateral aceito: um texto de milhar sem decimais com separador final (ex.: "1.234" em
 * pt-BR) tem cauda de 3 dígitos e fica intacto, mas com `maxDecimals` < 3 seria cortado — por
 * isso só usar teto menor que 3 em textos sem agrupamento.
 */
export function truncateDecimalText(text: string, maxDecimals = MAX_DISPLAY_DECIMALS): string {
  const sep = Math.max(text.lastIndexOf('.'), text.lastIndexOf(','));
  if (sep < 0) return text;
  const tail = text.slice(sep + 1);
  if (tail.length <= maxDecimals || !/^\d+$/.test(tail)) return text;
  // maxDecimals 0 remove também o separador
  return text.slice(0, maxDecimals > 0 ? sep + 1 + maxDecimals : sep);
}
